import { action, makeAutoObservable, observable } from 'mobx';
import request from '@stores/decorators/request';
import AccApplyRepo, { RemoteAccApplyRepo } from '@repository/acc/apply/AccApplyRepo';
import AccRepo, { RemoteAccRepo } from '@repository/acc/AccRepo';
import AccCompetenceRepo, { RemoteAccCompetenceRepo } from '@repository/acc/competence/AccCompetenceRepo';
import { AccResultRecordVO, AccResultVO } from '@domain/vo/acca/v1/test/result/AccResultVO';
import { AccApplyResultVO } from '@domain/vo/acca/v1/test/result/apply/AccApplyResultVO';
import { AccApplyTipVO } from '@domain/vo/acca/v1/test/result/apply/AccApplyTipVO';
import { AccGameResultVO } from '@domain/vo/acca/v1/test/result/apply/AccGameResultVO';
import { AccSelfReportResultVO } from '@domain/vo/acca/v1/test/result/apply/AccSelfReportResultVO';
import { AccVideoDetailResultVO, AccVideoResultVO } from '@domain/vo/acca/v1/test/result/apply/AccVideoResultVO';
import { AccCompanyCurationVO } from '@domain/vo/acca/v1/test/result/competence/AccCompanyCurationVO';
import { AccJobCurationVO } from '@domain/vo/acca/v1/test/result/competence/AccJobCurationVO';
import { AccProfileResultVO } from '@domain/vo/acca/v1/test/result/competence/AccProfileResultVO';
import { AccApplyCreateRs } from '@domain/rs/acca/v1/test/result/AccApplyCreateRs';
import { VideoType } from '@domain/constant/acca/test/result/videoType';

export default class AccaTestResultStore {
  private readonly repo: AccApplyRepo;
  private readonly accRepo: AccRepo;
  private readonly accCompetenceRepo: AccCompetenceRepo;

  /** 응시 내역 **/
  @observable accResults: AccResultVO | null = null; // 역검 결과 리스트
  @observable maxApplyCount = 0; // 응시 최대 횟수

  /** 신역검 걸과 **/
  @observable userName: string | null = '';
  @observable accResultSn = -1;
  @observable accResult: AccResultRecordVO | null = null; // 역검 결과

  /** 신역검 응시 분석 결과 **/
  @observable accApplyResult: AccApplyResultVO | null = null;
  @observable accSelfReportResult: AccSelfReportResultVO | null = null;
  @observable accVideoResult: AccVideoResultVO | null = null;
  @observable accVideoDetailMap: Map<VideoType, AccVideoDetailResultVO[]> | null = null;
  @observable accGameResult: AccGameResultVO | null = null;
  @observable accApplyTip: AccApplyTipVO | null = null;

  /** 신역검 역량 분석 결과 **/
  @observable accProfileResult: AccProfileResultVO | null = null;
  @observable accCompanyCuration: AccCompanyCurationVO | null = null;
  @observable accJobCuration: AccJobCurationVO | null = null;

  constructor() {
    this.repo = new RemoteAccApplyRepo();
    this.accRepo = new RemoteAccRepo();
    this.accCompetenceRepo = new RemoteAccCompetenceRepo();
    makeAutoObservable(this, {}, { autoBind: true });
  }

  @action.bound
  setUserName = (userName: string) => {
    this.userName = userName;
  };

  @action.bound
  setAccResultSn = (accResultSn: number) => {
    this.accResultSn = accResultSn;
    this.accResult =
      this.accResults?.resultRecords?.find((resultRecord) => {
        return resultRecord.sn === accResultSn;
      }) || null;
  };

  // 신역검 응시이력

  /** 신역검 걸과 **/
  @action.bound
  @request
  async fetchAccResults() {
    const { data } = await this.accRepo.fetchAccResults();
    this.accResults = data;
  }

  @action.bound
  @request
  async fetchAccResultsAnalysisComplete() {
    const { data } = await this.accRepo.fetchAccResultsAnalysisComplete();
    return data;
  }

  @action.bound
  @request
  async fetchAccMaxApplyChance() {
    const { data } = await this.accRepo.fetchAccMaxApplyChance();
    return (this.maxApplyCount = data.maxApplyChance);
  }

  @action.bound
  @request
  async fetchAccApply() {
    try {
      const { data } = await this.repo.fetchAccApply();
      return data;
    } catch (e: any) {
      let errorCode = null;

      if (e?.response?.data?.errorCode) {
        errorCode = e?.response?.data?.errorCode;
      }

      if (e?.response?.status >= 500) {
        errorCode = 'B001';
      }

      return {
        code: errorCode,
        name: 'ERROR',
        sn: 0,
        url: '',
      } as AccApplyCreateRs;
    }
  }

  /** 신역검 응시 분석 결과 **/
  @action.bound
  @request
  async fetchAccApplyResult() {
    const { data } = await this.repo.fetchAccApplyResult(this.accResultSn);
    this.accApplyResult = data;
  }

  @action.bound
  @request
  async fetchAccApplySelfReport() {
    const { data } = await this.repo.fetchAccApplySelfReport(this.accResultSn);
    this.accSelfReportResult = data;
  }

  @action.bound
  @request
  async fetchAccApplyVideo() {
    const { data } = await this.repo.fetchAccApplyVideo(this.accResultSn);
    this.accVideoResult = data;

    this.accVideoDetailMap = new Map<VideoType, AccVideoDetailResultVO[]>();
    this.accVideoResult.details?.forEach((detail) => {
      const { type } = detail;
      this.accVideoDetailMap?.set(type, [...(this.accVideoDetailMap?.get(type) || []), detail]);
    });
  }

  @action.bound
  @request
  async fetchAccApplyGame() {
    const { data } = await this.repo.fetchAccApplyGame(this.accResultSn);
    this.accGameResult = data;
  }

  @action.bound
  @request
  async fetchAccApplyTip() {
    const { data } = await this.repo.fetchAccApplyTip(this.accResultSn);
    this.accApplyTip = data;
  }

  /** 신역검 역량 분석 결과 **/
  @action.bound
  @request
  async fetchAccCompetenceProfile() {
    const { data } = await this.accCompetenceRepo.fetchAccCompetenceProfile(this.accResultSn);
    this.accProfileResult = data;
  }

  @action.bound
  @request
  async fetchAccCompetenceCurationCompany() {
    try {
      const { data } = await this.accCompetenceRepo.fetchAccCompetenceCurationCompany(this.accResultSn);
      this.accCompanyCuration = data;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  @request
  async fetchAccCompetenceCurationJob() {
    const { data } = await this.accCompetenceRepo.fetchAccCompetenceCurationJob(this.accResultSn);
    this.accJobCuration = data;
  }
}
